import { useState } from "react"
import { faCalendarCheck, faClose, faClock, faPeopleGroup } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import type { RestaurantReservationSettings } from "../../../Objects/RestaurantReservation/RestaurantReservationTypes"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"

interface ReservationSettingsPopupProps {
  settings: RestaurantReservationSettings
  isLoading: boolean
  onClose: () => void
  onSave: (settings: RestaurantReservationSettings) => Promise<boolean>
}

export const ReservationSettingsPopup: React.FC<ReservationSettingsPopupProps> = ({
  settings,
  isLoading,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation("restaurantReservations")
  const [draft, setDraft] = useState(() => ({ ...settings }))

  const fields = [
    ["minimumAdvanceMinutes", "fields.minimum_advance", faClock, 0],
    ["maximumAdvanceDays", "fields.maximum_days", faCalendarCheck, 1],
    ["slotIntervalMinutes", "fields.slot_interval", faClock, 1],
    ["reservationDurationMinutes", "fields.duration", faClock, 1],
    ["minimumPartySize", "fields.minimum_party", faPeopleGroup, 1],
    ["maximumPartySize", "fields.maximum_party", faPeopleGroup, 1],
  ] as const

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("management.settings_title")}
              icon={faCalendarCheck}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <PopupHelpButton
                    helpKey="restaurant-reservation-settings"
                    variant="global"
                    title={t("help.settings.title")}
                    description={t("help.settings.description")}
                    bullets={t("help.settings.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:restaurantReservations.settingsPopup.close"
                  />
                </div>
              }
            />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
              <form
                className="p-2"
                onSubmit={async (event) => {
                  event.preventDefault()
                  if (await onSave(draft)) onClose()
                }}
              >
                <SimpleToggle
                  title={t("fields.reservations_enabled")}
                  value={draft.isEnabled}
                  onChange={(value) => setDraft((current) => ({ ...current, isEnabled: value }))}
                  descriptionKey="interactionDescriptions:restaurantReservations.settingsPopup.enabled"
                />

                <div className={defaultStyle.divisionBar} />

                <div className="grid gap-2 md:grid-cols-2">
                  {fields.map(([field, label, icon, min]) => (
                    <InputField
                      key={field}
                      type="number"
                      icon={icon}
                      value={String(draft[field])}
                      min={min}
                      required
                      placeholder={t(label)}
                      onInvalidMessage={t("validation.positive_number")}
                      onChange={(event) => setDraft((current) => ({ ...current, [field]: Number(event.target.value) }))}
                      descriptionKey="interactionDescriptions:restaurantReservations.settingsPopup.field"
                    />
                  ))}
                </div>

                <div className={defaultStyle.divisionBar} />

                <div className={defaultStyle.buttonsController}>
                  <Button
                    label={isLoading ? t("common.saving") : t("actions.save_settings")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    disabled={isLoading}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:restaurantReservations.settingsPopup.save"
                  />
                </div>
              </form>
            </Animate>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
